import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const PROBE_TIMEOUT_MS = 5000;
const CONFIRMATION_DELAY_MS = 2000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Returns true when the health endpoint says the API is usable.
// "degraded" now means DB unreachable OR widespread multi-user failures (server-side logic).
const probeHealthOnce = async () => {
  try {
    const response = await axios.get('/api/health', {
      timeout: PROBE_TIMEOUT_MS,
      validateStatus: () => true,
    });

    if (
      response &&
      (response.status === 500 ||
        response.status === 502 ||
        response.status === 503 ||
        response.status === 504)
    ) {
      return false;
    }

    if (
      response &&
      response.data &&
      (response.data.status === 'degraded' || response.data.database === false)
    ) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

const useApiHealthCheck = () => {
  const [apiIsDown, setApiIsDown] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const confirmationInProgress = useRef(false);

  // Two consecutive failed probes are required before declaring the site down,
  // so one transient blip or one user's slow query never blanks the app (MPI-737).
  const confirmApiDown = useCallback(async () => {
    if (confirmationInProgress.current) {
      return;
    }
    confirmationInProgress.current = true;

    try {
      if (await probeHealthOnce()) {
        setApiIsDown(false);
        return;
      }

      await delay(CONFIRMATION_DELAY_MS);

      const secondProbeHealthy = await probeHealthOnce();
      setApiIsDown(!secondProbeHealthy);
    } finally {
      confirmationInProgress.current = false;
    }
  }, []);

  const checkApiHealth = useCallback(async () => {
    const healthy = await probeHealthOnce();

    if (healthy) {
      setApiIsDown(false);
      setIsChecking(false);
      return true;
    }

    await delay(CONFIRMATION_DELAY_MS);
    const secondProbeHealthy = await probeHealthOnce();

    setApiIsDown(!secondProbeHealthy);
    setIsChecking(false);
    return secondProbeHealthy;
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Aborted by the caller (e.g. React Query cancelling) — not an outage.
        if (axios.isCancel(error) || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
          return Promise.reject(error);
        }

        // Never react to the health endpoint's own failures or the activation email endpoint.
        if (
          error.config &&
          error.config.url &&
          (error.config.url.includes('/api/health') ||
            error.config.url.includes('api/UserProgram/ResendOktaActivationEmail'))
        ) {
          return Promise.reject(error);
        }

        // Only infrastructure-level signals start an outage confirmation:
        // network unreachable, 502 bad gateway, 503 unavailable, 504 gateway timeout.
        // 4xx (incl. 408 query timeouts) and 500 application errors stay scoped to the
        // request that caused them.
        const status = error.response && error.response.status;
        const isInfrastructureSignal =
          !error.response || status === 502 || status === 503 || status === 504;

        if (isInfrastructureSignal) {
          confirmApiDown();
        }

        return Promise.reject(error);
      }
    );

    checkApiHealth();

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [checkApiHealth, confirmApiDown]);

  const handleRetry = useCallback(() => {
    setIsChecking(true);
    checkApiHealth();
  }, [checkApiHealth]);

  return {
    apiIsDown,
    isChecking,
    handleRetry,
    checkApiHealth,
  };
};

export default useApiHealthCheck;